import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { map, get, find, head } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Table } from 'rsuite';
import { getShowDeleteModal } from '../../redux/selectors/components/report-template-list';
import {
  getIsDeleting,
  getIsDeleted
} from '../../redux/selectors/report-template';
import {
  showConfirmDeleteReportTemplate,
  hideConfirmDeleteReportTemplate,
  resetDeleteReportTemplate
} from '../../redux/actions';
import { deleteReportTemplate } from '../../redux/sagas/report-template/routines';
import DeleteReportTemplateModal from '../DeleteReportTemplateModal';
import ActionCell from './CustomCells/ActionCell';
import getExtraColumnsForTemplate from './getExtraColumnsForTemplate';
import CustomHeaderCell from '../widgets/custom-cell/HeaderCell';

const { Column, Cell, HeaderCell } = Table;

const ReportTemplatesList = ({ data, loading, onSelect, ...props }) => {
  const dispatch = useDispatch();
  const [showActions, setShowActions] = useState(true);
  const [itemToDelete, setItemToDelete] = useState(null);
  const showDeleteModal = useSelector(getShowDeleteModal);
  const isDeleting = useSelector(getIsDeleting);
  const isDeleted = useSelector(getIsDeleted);

  const onHideConfirmDeleteModal = () => {
    dispatch(hideConfirmDeleteReportTemplate());
  };

  const handleSelect = id => {
    const template = find({ id }, data);
    onSelect(template);
  };

  const handleShowConfirmDelete = value => {
    setItemToDelete(value);
    dispatch(showConfirmDeleteReportTemplate());
  };

  const handleHideConfirmDelete = () => {
    setItemToDelete(null);
    onHideConfirmDeleteModal();
  };

  const handleConfirmDelete = () => {
    dispatch(deleteReportTemplate(itemToDelete));
  };

  useEffect(
    () => {
      if (isDeleted) {
        handleHideConfirmDelete();
        dispatch(resetDeleteReportTemplate());
      }
    },
    [isDeleted]
  );

  useEffect(() => {
    if (!onSelect) setShowActions(false);
  }, []);

  const templateType = get('fileType', head(data));
  const extraColumns = getExtraColumnsForTemplate(templateType);

  return (
    <Fragment>
      <Table data={data} loading={loading} autoHeight {...props}>
        <Column width={220}>
          <CustomHeaderCell>Name</CustomHeaderCell>
          <Cell dataKey="fileName" />
        </Column>
        {map(
          ({ title, width, cell }) => (
            <Column width={width} key={title} resizable>
              <CustomHeaderCell>{title}</CustomHeaderCell>
              {cell}
            </Column>
          ),
          extraColumns
        )}
        {showActions && (
          <Column width={120} align="center">
            <HeaderCell>Action</HeaderCell>
            <ActionCell
              dataKey="id"
              handleSelect={handleSelect}
              handleDelete={handleShowConfirmDelete}
            />
          </Column>
        )}
      </Table>

      {itemToDelete && (
        <DeleteReportTemplateModal
          show={showDeleteModal}
          item={itemToDelete}
          isDeleting={isDeleting}
          onHide={onHideConfirmDeleteModal}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Fragment>
  );
};

ReportTemplatesList.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
  onSelect: PropTypes.func
};

ReportTemplatesList.defaultProps = {
  onSelect: null
};

export default ReportTemplatesList;
